import { API } from "@swivl-mono/api-helper";
import { HolidayRuleInterface, ScheduleGroup } from "@swivl-mono/types";
import { useEffect, useState } from "react";

import { Member } from "../../../../../Models/Member/Member.Current";
import { HolidayRuleList } from "./HolidayRuleList";
import { CardEditAction, CardSaveActions, ScheduleCard } from "./ScheduleCard";
import { PageScope, visibleHolidays } from "./Scope";

interface HolidaysSectionProps {
  scope: PageScope;
  /** Every schedule group a rule may close — built-ins plus the org's custom groups. */
  groups: ScheduleGroup[];
}

/**
 * Page-level holiday rules. Rules are slug-targeted via `appliesToSlugs`, so
 * Christmas can close the office and call centre while gate access stays open.
 *
 * At the location scope this lists every applicable rule (location + group +
 * org) with provenance tags; at group / org it lists only rules at-or-broader
 * than the current scope.
 *
 * Adds and removals stage into this card and are written on Save, matching the
 * schedule cards, so nothing is persisted until the operator commits.
 */
export function HolidaysSection({ scope, groups }: HolidaysSectionProps): React.ReactElement | null {
  const orgId = Member.orgId;

  const [rules, setRules] = useState<HolidayRuleInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staged, setStaged] = useState<Partial<HolidayRuleInterface>[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    setLoading(true);
    try {
      let next: HolidayRuleInterface[] = [];
      if (scope.kind === "location" && scope.locationId != null) {
        next = await API.Schedule.listApplicableHolidayRulesForLocation(orgId, scope.locationId);
      } else if (scope.kind === "group" && scope.groupId != null) {
        // Visible: org + this group's rules.
        const [grp, org] = await Promise.all([
          API.Schedule.listHolidayRulesAtScope(orgId, { groupId: scope.groupId }),
          API.Schedule.listHolidayRulesAtScope(orgId, {})
        ]);
        next = [...grp, ...org];
      } else {
        next = await API.Schedule.listHolidayRulesAtScope(orgId, {});
      }
      setRules(visibleHolidays(next, scope));
      setStaged([]);
      setDeletedIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, scope.kind, scope.groupId, scope.locationId]);

  if (!orgId) return null;

  const handleCancel = (): void => {
    setStaged([]);
    setDeletedIds([]);
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      for (const rule of staged) {
        await API.Schedule.upsertHolidayRule(orgId, rule);
      }
      for (const ruleId of deletedIds) {
        await API.Schedule.deleteHolidayRule(orgId, ruleId);
      }
      await refresh();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScheduleCard
      title="Holidays"
      caption="Recurring closures. Each rule picks which schedules it affects."
      error={error}
      loading={loading}
      actions={
        isEditing ? (
          <CardSaveActions saving={saving} canSave={staged.length > 0 || deletedIds.length > 0} onCancel={handleCancel} onSave={handleSave} />
        ) : (
          <CardEditAction onEdit={() => setIsEditing(true)} />
        )
      }
    >
      <HolidayRuleList
        rules={rules}
        staged={staged}
        deletedIds={deletedIds}
        scope={scope}
        groups={groups}
        isEditing={isEditing}
        onStage={(rule) => setStaged((prev) => [...prev, rule])}
        onUnstage={(index) => setStaged((prev) => prev.filter((_, i) => i !== index))}
        onDelete={(ruleId) => setDeletedIds((prev) => [...prev, ruleId])}
      />
    </ScheduleCard>
  );
}
